import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { getAuthConfig, getApiBaseUrl } from "../../services/configService";
import "./AuthForm.css";

const NEW_USER = "__NEW__";

const AuthForm = () => {
  const [records, setRecords] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    email: "",
    revenue_ids: "",
    is_admin: false,
    is_ffc: false,
    is_leadership: false,
  });

  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const getToken = async () => {
    const res = await instance.acquireTokenSilent({
      account: accounts[0],
      scopes: [`api://${authConfig.clientId}/access_as_user`],
    });
    return res.accessToken;
  };

  /* ======================
     FETCH DATA
  ====================== */
  const fetchData = async () => {
    const token = await getToken();
    const res = await fetch(`${getApiBaseUrl()}/admin/argus-auth`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    setRecords(json || []);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ======================
     SIDEBAR (group by email)
  ====================== */
  const grouped = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!map[r.email]) map[r.email] = [];
      map[r.email].push(r);
    });
    return map;
  }, [records]);

  const filteredUsers = useMemo(() => {
    return Object.keys(grouped).filter((email) =>
      email.toLowerCase().includes(search.toLowerCase())
    );
  }, [grouped, search]);

  /* ======================
     SELECT USER
  ====================== */
  const handleSelect = (email) => {
    setSelectedEmail(email);

    const userRows = grouped[email] || [];

    const is_admin = userRows.some((r) => r.is_admin);
    const is_ffc = userRows.some((r) => r.is_ffc);
    const is_leadership = userRows.some((r) => r.is_leadership);

    const revenue_ids = is_admin
      ? "ADMIN"
      : userRows.map((r) => r.revenue_id).join(", ");

    setForm({
      email,
      revenue_ids,
      is_admin,
      is_ffc,
      is_leadership,
    });
  };

  /* ======================
     VALIDATION + ROW BUILD
  ====================== */

  const buildRows = () => {
    let value;
  
    if (form.is_admin) {
      value = "ADMIN";
    } else {
      value = form.revenue_ids
        .replace(/\s+/g, "")
        .toUpperCase();
    }
  
    return [
      {
        email: form.email,
        revenue_id: value,
        is_admin: form.is_admin,
        is_ffc: form.is_ffc,
        is_leadership: form.is_leadership,
      }
    ];
  };

  /* ======================
     BUSINESS RULES
  ====================== */

  const enforceRules = (updated) => {
    if (updated.is_admin) {
      return { ...updated, is_ffc: false, is_leadership: false, revenue_ids: "ADMIN" };
    }

    if (updated.is_ffc) {
      return { ...updated, is_admin: false, is_leadership: false };
    }

    if (updated.is_leadership) {
      return { ...updated, is_admin: false, is_ffc: false };
    }

    return updated;
  };

  const updateExisting = async (rows) => {
    const token = await getToken();
  
    const existingRows = grouped[selectedEmail] || [];
  
    for (let i = 0; i < existingRows.length; i++) {
      const record = existingRows[i];
  
      if (rows[i]) {
        await fetch(
          `${getApiBaseUrl()}/admin/argus-auth/${record.id}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(rows[i]),
          }
        );
      }
    }
  
    if (rows.length > existingRows.length) {
      for (let i = existingRows.length; i < rows.length; i++) {
        await fetch(`${getApiBaseUrl()}/admin/argus-auth`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(rows[i]),
        });
      }
    }
   
    fetchData();
  };


  const confirmCreate = async () => {
    const token = await getToken();
  
    for (const row of pendingRows) {
      const res = await fetch(`${getApiBaseUrl()}/admin/argus-auth`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(row),
      });
  
      if (!res.ok) {
        const text = await res.text();
        console.error("CREATE FAILED:", text); 
      }
    }
  
    setShowCreateConfirm(false);
    setSelectedEmail(null);
    fetchData();
  };
  

  /* ======================
     CREATE
  ====================== */

  const handleSubmit = () => {
    const rows = buildRows();
  
    if (selectedEmail === NEW_USER) {
      setPendingRows(rows);
      setShowCreateConfirm(true);
    } else {
      updateExisting(rows);
    }
  };

  /* ======================
     DELETE
  ====================== */

  const confirmDelete = async () => {
    const token = await getToken();

    await fetch(
      `${getApiBaseUrl()}/admin/argus-auth/${deleteTarget.id}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    setDeleteTarget(null);
    fetchData();
  };

  return (
    <div className="auth-page">

      <div className="auth-sidebar">

        <div className="auth-sidebar-header">
          <button
            className="back-btn-auth"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            title="Back"
          >
            <FaArrowLeft />
          </button>
        
          <input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
          />

          <button
              className="new-user-btn"
              onClick={() => {
              setSelectedEmail(NEW_USER);
              setForm({
                  email: "",
                  revenue_ids: "",
                  is_admin: false,
                  is_ffc: false,
                  is_leadership: false,
              });
              }}
          >
              + New User
          </button>
        </div>


        <div className="auth-sidebar-list">
            {filteredUsers.map((email) => {

                const rows = grouped[email] || [];

                const base = email.split('@')[0];
                const displayName = base
                .split('.')
                .map(p => p.charAt(0).toUpperCase() + p.slice(1))
                .join(' ');

                const isAdmin = rows.some(r => r.is_admin);
                const isFfc = rows.some(r => r.is_ffc);
                const isLead = rows.some(r => r.is_leadership);

                return (
                <div
                    key={email}
                    className={`auth-card ${selectedEmail === email ? "selected" : ""}`}
                    onClick={() => handleSelect(email)}
                >
                    <div className="auth-card-name">{displayName}</div>

                    <div className="auth-card-email">{email}</div>

                    <div className="auth-card-footer">

                    <div className="role-group">
                        {isAdmin && <span className="role-badge admin">Admin</span>}
                        {isFfc && <span className="role-badge ffc">FFC</span>}
                        {isLead && <span className="role-badge leadership">Leadership</span>}
                        {!isAdmin && !isFfc && !isLead && <span className="role-badge citizen">Citizen</span>}
                    </div>

                    <span className="row-count">{rows.length} entries</span>

                    </div>
                </div>
                );
            })}
        </div>

      </div>


      {/* MAIN */}
      <div className="auth-main">

        {!selectedEmail && (

            <div className="auth-instructions">

                <div className="auth-intro">
                <h2>Authorization Workbench</h2>
                <p>
                    Manage user access across ARGUS by assigning roles and defining hospital (Revenue ID)
                    permissions. This tool controls what data each user can see and interact with.
                </p>
                </div>

                <div className="auth-instruction-grid">

                <div className="auth-instruction-card">
                    <div className="step">1</div>
                    <div>
                    <strong>Select or Create a User</strong>
                    <p>
                        Use the search bar to quickly find a user. Click any user card to edit permissions,
                        or click <b>+ New User</b> to create a new authorization profile.
                    </p>
                    </div>
                </div>

                <div className="auth-instruction-card">
                    <div className="step">2</div>
                    <div>
                    <strong>Assign Roles</strong>
                    <p>
                        Choose between Admin, FFC, or Leadership roles. Selecting a role automatically enforces
                        valid combinations to prevent conflicting permissions.
                    </p>
                    </div>
                </div>

                <div className="auth-instruction-card">
                    <div className="step">3</div>
                    <div>
                    <strong>Set Access Scope</strong>
                    <p>
                        Enter one or more Revenue IDs separated by commas (e.g. AVD, NCS). Values are cleaned,
                        capitalized, and validated before saving.
                    </p>
                    </div>
                </div>

                <div className="auth-instruction-card">
                    <div className="step">4</div>
                    <div>
                    <strong>Manage Entries</strong>
                    <p>
                        Each Revenue ID creates an individual access record. Existing entries are listed below
                        and can be removed individually as needed.
                    </p>
                    </div>
                </div>

                </div>

            </div>

        )}

        {selectedEmail && (
            <div className="auth-form">

                <div className="workbench-header">
                    <div className="workbench-header-title">
                        {selectedEmail ? (
                        <>Editing <span>{selectedEmail}</span></>
                        ) : (
                        <>Create New Authorization</>
                        )}
                    </div>

                    <button className="save-btn" onClick={handleSubmit}>
                        Save Changes
                    </button>
                    </div>

                    <div className="auth-grid">

                    <div className="form-group">
                        <label>Email</label>
                        <input
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>Revenue IDs</label>
                        <textarea
                        value={form.revenue_ids}
                        disabled={form.is_admin}
                        placeholder="e.g. AVD, NCS, PHX"
                        onChange={e =>
                            setForm({ ...form, revenue_ids: e.target.value })
                        }
                        />
                    </div>

                    </div>

                    {/* ROLE TOGGLES */}
                    <div className="role-toggles">

                    {[
                        { key: "is_admin", label: "Admin" },
                        { key: "is_ffc", label: "FFC" },
                        { key: "is_leadership", label: "Leadership" },
                    ].map(r => (
                        <span
                        key={r.key}
                        className={`toggle-pill ${form[r.key] ? "active" : ""}`}                      
                        onClick={() => {
                          const isCurrentlyActive = form[r.key];

                          if (isCurrentlyActive) {
                            setForm({
                              ...form,
                              is_admin: false,
                              is_ffc: false,
                              is_leadership: false,
                            });
                          } else {
                            setForm(enforceRules({
                              ...form,
                              is_admin: r.key === "is_admin",
                              is_ffc: r.key === "is_ffc",
                              is_leadership: r.key === "is_leadership",
                            }));
                          }
                        }}
                        >
                        {r.label}
                        </span>
                    ))}

                    </div>

                    <div className="auth-rows">
                      <div className="rows-header">Existing Entries</div>

                      {(grouped[selectedEmail] || []).map((row) => (
                        <div key={row.id} className="auth-row">
                          <span className="row-rev">{row.revenue_id}</span>

                          <button
                            className="delete-btn"
                            onClick={() => setDeleteTarget(row)}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>

                </div>

        )}

      </div>

      {/* CREATE MODAL */}
      {showCreateConfirm && (
        <div className="modal">
          <div className="modal-box">
            <h3>Confirm Create</h3>

            {pendingRows.map((r, i) => (
              <p key={i}>{r.email} — {r.revenue_id}</p>
            ))}

            <div className="modal-actions">
              <button
                className="modal-cancel"
                onClick={() => setShowCreateConfirm(false)}
              >
                Cancel
              </button>

              <button
                className="modal-confirm create"
                onClick={confirmCreate}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal">
          <div className="modal-box">
            <h3>Confirm Delete</h3>

            <p>
              You are about to delete:
              <br />
              <b>{deleteTarget.email}</b> — {deleteTarget.revenue_id}
            </p>

            <div className="modal-actions">
              <button
                className="modal-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>

              <button
                className="modal-confirm delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AuthForm;